import { useContext, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { UPDATE_CUSTOMER_EMAIL_ADDRESS } from "../../../mutations/UpdateCustomerEmailAddress/updateCustomerEmailAddress.mutation";
import { GET_ACTIVE_USER } from "../../../queries/auth.queries";
import { AccountContext } from "../../../context/account.context";
import {
  UpdateCustomerEmailAddressResponse,
  UpdateCustomerEmailAddressVariables,
} from "./change-email-address.types";

interface ChangeEmailAddressState {
  status: "loading" | "success" | "error";
  message: string;
}

export const useChangeEmailAddress = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const { accountState } = useContext(AccountContext) as unknown as {
    accountState: { isUser: boolean };
  };

  const token = new URLSearchParams(location.search).get("token");

  const [state, setState] = useState<ChangeEmailAddressState>({
    status: "loading",
    message: t("auth.changingEmail"),
  });

  const [updateCustomerEmailAddress] = useMutation<
    UpdateCustomerEmailAddressResponse,
    UpdateCustomerEmailAddressVariables
  >(UPDATE_CUSTOMER_EMAIL_ADDRESS, {
    refetchQueries: [{ query: GET_ACTIVE_USER }],
  });

  useEffect(() => {
    if (!token) {
      setState({ status: "error", message: t("auth.invalidChangeEmailLink") });
      return;
    }

    updateCustomerEmailAddress({ variables: { token } })
      .then((result) => {
        const updateResult = result?.data?.updateCustomerEmailAddress;

        if (updateResult?.__typename === "Success" && updateResult.success) {
          setState({ status: "success", message: t("auth.emailChanged") });
          return;
        }

        setState({
          status: "error",
          message:
            (updateResult as { message?: string } | undefined)?.message ??
            t("errors.generic"),
        });
      })
      .catch((err) => {
        setState({
          status: "error",
          message: (err as Error)?.message || t("errors.generic"),
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onContinueToAccount = () => {
    history.push(accountState.isUser ? "/account/settings" : "/");
  };

  return {
    ...state,
    onContinueToAccount,
  };
};
